import { categorizeAsset } from '@odo/shared-ui';
import type { Asset, FileUploader } from '@odo/shared-ui';
import api, { files_api } from '../api/config';
import apiV2 from '../api/v2';
import { createImageThumbnail } from './imageThumbnail';

// The Asset shape now lives in the shared files module; re-exported here so
// the rest of the app keeps importing it from the service layer.
export type { Asset, FileUploader };

export interface PatientCloudFile {
    id: string;
    file: {
        originalFilename: string;
        mimeType: string;
        createdAt: string;
        sizeBytes: string;
    };
    uploadedBy?: FileUploader | null;
    thumbUrl?: string | null;
    photoType?: string | null;
    /** Coarse storage category (e.g. PHOTO, RADIOGRAPH, STL). */
    fileType?: string | null;
    /** Free-form metadata from `PatientFile.metadata` (e.g. CBCT manifest/binary refs). */
    metadata?: Record<string, unknown> | null;
    internal?: boolean;
}

export interface UploadResult {
    success: boolean;
    error?: string;
    /** Original error stack — preserved so callers can surface it in error reports */
    errorStack?: string;
    /** Cloud-file id of the uploaded file (set on success). Lets callers fetch a
     *  download URL without re-listing assets. */
    patientFileId?: string;
    /** Underlying storage file id of the uploaded file (set on success). */
    fileId?: string;
}

export const fetchPatientCloudFiles = async (patientId: string): Promise<PatientCloudFile[]> => {
    const response = await api.get<PatientCloudFile[]>(`/patients/${patientId}/cloud-files`);
    return response.data;
};

export const fetchCloudFileDownloadUrl = async (
    patientId: string,
    patientFileId: string
): Promise<string> => {
    const response = await api.get(
        `/patients/${patientId}/cloud-files/${patientFileId}/download-url`
    );
    return response.data.downloadUrl;
};

export const fetchThumbnailUploadUrl = async (
    patientId: string,
    fileId: string
): Promise<string> => {
    const response = await api.post(`/patients/${patientId}/cloud-files/thumbnail-upload-url`, {
        fileId
    });
    return response.data.uploadUrl;
};

export async function uploadThumbnail(
    patientId: string,
    fileId: string,
    file: File
): Promise<void> {
    const thumbnail = await createImageThumbnail(file);
    if (!thumbnail) return;

    // The thumbnail endpoint keys off the storage file id (not the patient_file id).
    const uploadUrl = await fetchThumbnailUploadUrl(patientId, fileId);

    const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: thumbnail
    });
    if (!response.ok) throw new Error(`Thumbnail upload failed with status ${response.status}`);
}

export const fetchFileViaProxy = async (url: string, filename: string): Promise<Blob> => {
    const response = await files_api.get('/contents/file-proxy', {
        params: { url, filename },
        responseType: 'blob'
    });
    return response.data;
};

export const createCloudFileUpload = async (
    patientId: string,
    file: { name: string; type: string; size: number },
    metadata?: Record<string, unknown>,
    fileType?: string
): Promise<{ uploadUrl: string; patientFileId: string; fileId: string }> => {
    const response = await api.post(`/patients/${patientId}/cloud-files/upload`, {
        originalFilename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        description: 'Uploaded via Patient Files',
        ...(fileType ? { fileType } : {}),
        ...(metadata ? { metadata } : {})
    });
    return response.data;
};

export const completeCloudFileUpload = async (
    patientId: string,
    patientFileId: string
): Promise<void> => {
    await api.post(`/patients/${patientId}/cloud-files/${patientFileId}/complete`);
};

// Re-signs the upload URL for an in-flight upload (file still in UPLOADING
// status). Used by upload retries that may outlive the 10-minute expiry of
// the original signed URL.
export const refreshCloudFileUploadUrl = async (
    patientId: string,
    patientFileId: string
): Promise<{ uploadUrl: string }> => {
    const response = await api.post(
        `/patients/${patientId}/cloud-files/${patientFileId}/upload-url`
    );
    return response.data;
};

// Maximum number of retry attempts after the initial try (total = MAX_RETRIES + 1 attempts)
const GCS_UPLOAD_MAX_RETRIES = 2;
// Base delay in ms — multiplied by attempt index (1 s, 2 s)
const GCS_UPLOAD_RETRY_BASE_MS = 1000;

function gcsSleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

interface GcsError extends Error {
    /** HTTP status from GCS, 0 for network-level failure, -1 for abort */
    gcsHttpStatus: number;
}

function isGcsRetryable(status: number): boolean {
    return status === 0 || (status >= 500 && status < 600);
}

function attemptFileToGCS(
    file: File,
    uploadUrl: string,
    onProgress?: (progress: number) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                onProgress((event.loaded / event.total) * 100);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                const err = new Error(`Upload failed with status ${xhr.status}`) as GcsError;
                err.gcsHttpStatus = xhr.status;
                reject(err);
            }
        });

        xhr.addEventListener('error', () => {
            const err = new Error(
                'Network error occurred during upload. Please check your connection.'
            ) as GcsError;
            err.gcsHttpStatus = 0;
            reject(err);
        });

        xhr.addEventListener('abort', () => {
            const err = new Error('Upload was cancelled') as GcsError;
            err.gcsHttpStatus = -1;
            reject(err);
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
    });
}

export const uploadFileToGCS = async (
    patientId: string,
    file: File,
    onProgress?: (progress: number) => void,
    metadata?: Record<string, unknown>,
    fileType?: string
): Promise<UploadResult> => {
    try {
        const { uploadUrl, patientFileId, fileId } = await createCloudFileUpload(
            patientId,
            { name: file.name, type: file.type, size: file.size },
            metadata,
            fileType
        );

        // Retry loop — retries on network errors and 5xx; never on 4xx or abort.
        // Signed URLs expire after 10 minutes, so each retry fetches a fresh URL
        // first; if that fails, the previous URL is reused (no worse than not
        // refreshing).
        let lastError: Error | null = null;
        let currentUploadUrl = uploadUrl;
        for (let attempt = 0; attempt <= GCS_UPLOAD_MAX_RETRIES; attempt++) {
            if (attempt > 0) {
                await gcsSleep(GCS_UPLOAD_RETRY_BASE_MS * attempt);
                try {
                    const fresh = await refreshCloudFileUploadUrl(patientId, patientFileId);
                    currentUploadUrl = fresh.uploadUrl;
                } catch (refreshError) {
                    console.warn(
                        'Failed to refresh upload URL, retrying with the previous one:',
                        refreshError
                    );
                }
                onProgress?.(0);
            }
            try {
                await attemptFileToGCS(file, currentUploadUrl, onProgress);
                lastError = null;
                break;
            } catch (err) {
                lastError = err instanceof Error ? err : new Error(String(err));
                const httpStatus = (err as GcsError).gcsHttpStatus ?? -1;
                if (!isGcsRetryable(httpStatus) || attempt >= GCS_UPLOAD_MAX_RETRIES)
                    throw lastError;
            }
        }

        await completeCloudFileUpload(patientId, patientFileId);

        if (categorizeAsset(file.name) === 'Images') {
            // Awaited but non-fatal: the original upload has already completed, so a
            // thumbnail failure must never fail the upload.
            try {
                await uploadThumbnail(patientId, fileId, file);
            } catch (err) {
                // Best-effort: never fail the upload, but surface the failure so it
                // isn't a silent black hole (visible in devtools / any future SDK).
                console.warn('Thumbnail generation/upload failed (non-fatal):', err);
            }
        }

        return { success: true, patientFileId, fileId };
    } catch (error: unknown) {
        const errorObj = error as {
            response?: { data?: { message?: string } };
            message?: string;
            stack?: string;
        };
        return {
            success: false,
            error: errorObj?.response?.data?.message || errorObj?.message || 'Upload failed',
            errorStack: errorObj?.stack
        };
    }
};

export interface DeletePatientResult {
    patientId: string;
    practiceIdBefore: string | null;
    mondayDeleteSucceeded: boolean;
}

/**
 * Admin-only soft delete. Detaches the patient from its practice in the DB
 * (the patient is then filtered out of every listing) and best-effort deletes
 * the corresponding Monday item.
 */
export const deletePatient = async (patientId: string): Promise<DeletePatientResult> => {
    const response = await apiV2.delete<{ success: boolean; data: DeletePatientResult }>(
        `/patients/${patientId}`
    );
    return response.data.data;
};

export const updateCloudFile = async (
    patientId: string,
    patientFileId: string,
    changes: { originalFilename?: string; fileType?: string; metadata?: Record<string, unknown> }
): Promise<void> => {
    const body: {
        originalFilename?: string;
        fileType?: string;
        metadata?: Record<string, unknown>;
    } = {};
    if (changes.originalFilename !== undefined) body.originalFilename = changes.originalFilename;
    if (changes.fileType !== undefined) body.fileType = changes.fileType;
    if (changes.metadata !== undefined) body.metadata = changes.metadata;
    await api.patch(`/patients/${patientId}/cloud-files/${patientFileId}`, body);
};

// The backend copies the image into a new PROFILE_PICTURE file that becomes the avatar.
export const setCloudFileAsProfilePicture = async (
    patientId: string,
    patientFileId: string
): Promise<void> => {
    await api.post(`/patients/${patientId}/cloud-files/${patientFileId}/profile-picture`);
};

export interface CbctProcessingJob {
    jobId: string;
    status: string;
}

export interface CbctJobStatusInfo {
    jobId: string;
    status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
    errorMessage: string | null;
    createdAt: string;
    finishedAt: string | null;
}

// Latest CBCT processing job for a file, read by the backend from the shared
// processing.job table. Null when the file has never been dispatched.
export const fetchCbctProcessingStatus = async (
    patientId: string,
    patientFileId: string
): Promise<CbctJobStatusInfo | null> => {
    const response = await api.get<{ success: boolean; data: CbctJobStatusInfo | null }>(
        `/patients/${patientId}/cloud-files/${patientFileId}/process`
    );
    return response.data.data;
};

// Triggers CBCT encoder processing on odo-processing for a GCS-backed patient
// file. The backend rejects files whose stored fileType is not CBCT. An
// optional password is forwarded for scans stored as password-protected zips.
export const triggerCbctProcessing = async (
    patientId: string,
    patientFileId: string,
    password?: string
): Promise<CbctProcessingJob> => {
    const trimmed = password?.trim();
    const response = await api.post<{ success: boolean; data: CbctProcessingJob }>(
        `/patients/${patientId}/cloud-files/${patientFileId}/process`,
        trimmed ? { password: trimmed } : undefined
    );
    return response.data.data;
};

// Ends a patient's treatment and closes the case (Complete / Done) via the v2
// workflow engine. Admin end-treatment always closes as outside-ODO retainers.
export const endTreatment = async (patientId: string): Promise<void> => {
    await apiV2.post(
        `/patients/${encodeURIComponent(patientId)}/workflow/treatment/end-treatment`,
        {
            retainerOption: 'outside_odo'
        }
    );
};

// Archives a case (admin-only, global action) via the v2 workflow engine.
// Only reachable from an active status - the backend returns 409 if the
// case is already in a terminal (Done-phase) status.
export const archiveCase = async (patientId: string, reason: string): Promise<void> => {
    await apiV2.post(`/patients/${encodeURIComponent(patientId)}/workflow/terminal/archive`, {
        reason
    });
};
